import { travelDay, travelDayDate } from '../../lib/calendar'
import { isoDate } from '../../lib/format'
import { mergeTrackParts, nachFahrtzeit, trackKonflikt, trackParts } from '../../lib/gpx'
import { activeStages } from '../../lib/trip'
import type { DiaryEntry, DiaryTrack, Stage, TrackPart, Trip } from '../../lib/types'

/**
 * Eine Aufzeichnung über mehrere Tage auf die Etappen verteilen.
 *
 * Das Gerät hält 50 Stunden durch und hat 8 GB - die halbe Reise passt in
 * eine Datei. Zerschnitten wird sie in lib/gpx.ts (tracksByDay), hier steht die
 * zweite Hälfte der Aufgabe: welches Stück auf welche Etappe gehört, was
 * dabei im Weg steht und was am Ende geschrieben wird.
 *
 * Reine Funktionen, kein React: was sechzehn Etappen auf einmal ändert, gehört
 * unter Test. Die Bedienung dazu steht in TrackVerteilen.tsx, und sie schreibt
 * nichts, bevor der Nutzer nicht gesehen hat, was wohin geht.
 */

/**
 * Das Datum einer Etappe, als ISO-Datum.
 *
 * Zuerst das, was im Tagebuch steht: wer eingetragen hat, an welchem Tag er
 * diese Etappe gefahren ist, hat recht - die Reise verschiebt sich, und genau
 * dafür gibt es das Feld. Erst danach die Planung, und die ist einfach: eine
 * Etappe ist ein Tag, also Abfahrt plus Reisetag minus eins.
 *
 * Welcher Reisetag das ist, entscheidet lib/calendar.ts und nicht diese Datei -
 * dieselbe Rechnung wie für den Zeitraum auf der Titelseite des Buchs
 * (features/print/metrics.ts). Ausgelassene Etappen zählen dabei keinen Tag
 * mit; die Begründung steht dort.
 *
 * @returns Leer, wenn die Reise kein brauchbares Startdatum hat oder die Etappe
 *   ausgelassen ist. Dann gibt es nichts zuzuordnen, und ein erfundenes Datum
 *   wäre schlimmer als keines.
 */
export function stageDate(stage: Stage, trip: Trip, entry?: DiaryEntry): string {
  if (entry?.date) return entry.date
  return travelDayDate(trip.meta.start, travelDay(trip.stages, stage))
}

/** Etappe je Datum, nach der Reihenfolge der Etappen. */
export function stagesByDate(trip: Trip, diary: Record<string, DiaryEntry>): Map<string, Stage> {
  const map = new Map<string, Stage>()
  for (const stage of activeStages(trip)) {
    const datum = stageDate(stage, trip, diary[stage.id])
    // Die erste Etappe mit diesem Datum gewinnt. Zwei Etappen am selben Tag
    // gibt es, wenn jemand das Datum von Hand gesetzt hat; die zweite bleibt
    // ohne Vorschlag und lässt sich in der Liste von Hand wählen.
    if (datum && !map.has(datum)) map.set(datum, stage)
  }
  return map
}

/**
 * Was mit einem Stück auf der gewählten Etappe passieren würde.
 *
 * 'ohne-etappe' ist kein Fehler: ein Ruhetag ohne eigene Etappe, ein Datum vor
 * der Abfahrt oder ein Stück aus einer Datei, die auch die Heimfahrt enthält.
 * Solche Stücke bleiben in der Liste stehen, unangehakt und mit ihrem Datum -
 * verworfen wird nichts still, und wer will, wählt von Hand eine Etappe.
 */
export type ZuordnungArt = 'neu' | 'ergänzt' | 'gleich' | 'überlappt' | 'ohne-etappe'

/**
 * Beurteilt ein Stück gegen den Bestand einer Etappe.
 *
 * Dieselbe Regel wie beim Import einer einzelnen Datei, und das ist der Punkt:
 * ein Stück, dessen Zeitraum auf der Zieletappe schon liegt, darf nicht ein
 * zweites Mal in die Kilometer gehen - ob es nun von Hand oder aus einer
 * Mehrtagesdatei dorthin kommt.
 *
 * @param zusaetzlich Was in diesem Durchgang schon auf dieselbe Etappe gelegt
 *   wurde. Seit mehrere Dateien auf einmal gewählt werden können, ist das der
 *   häufigere Fall als der Bestand im Tagebuch: zwei Dateien können dieselbe
 *   Fahrt enthalten, und dann liegt das Stück, mit dem es sich beisst, noch gar
 *   nicht im Tagebuch, sondern zwei Zeilen weiter oben in derselben Liste.
 */
export function bewerte(
  track: DiaryTrack,
  stageId: string,
  diary: Record<string, DiaryEntry>,
  zusaetzlich: TrackPart[] = [],
): ZuordnungArt {
  if (!stageId) return 'ohne-etappe'

  const vorhanden = diary[stageId]?.track
  const teile = [...(vorhanden ? trackParts(vorhanden) : []), ...zusaetzlich]
  if (teile.length === 0) return 'neu'

  const konflikt = trackKonflikt(teile, trackParts(track)[0]!)
  return konflikt ? konflikt.art : 'ergänzt'
}

/** Ein Stück der Auswahl und das, was damit geschehen soll. */
export interface Zuordnung {
  /** Stelle in der Liste. Zugleich der Schlüssel in der Anzeige. */
  index: number
  track: DiaryTrack
  /** Der Tag, auf den das Stück fällt. Leer, wenn die Datei keine Uhr hat. */
  datum: string
  /** Die Zieletappe. Leer heisst: dieses Stück geht nirgendwohin. */
  stageId: string
  art: ZuordnungArt
  /** Ob es beim Übernehmen mitgeht. */
  aktiv: boolean
}

/**
 * Beurteilt die Zeilen der Reihe nach und merkt sich dabei, was schon vergeben
 * ist.
 *
 * Der Reihe nach und nicht jede für sich, seit mehrere Dateien auf einmal
 * gewählt werden können: zwei Dateien können dieselbe Fahrt enthalten -
 * versehentlich zweimal gewählt, oder Current.gpx neben der Einzeldatei -, und
 * dann steht das Stück, mit dem sich eine Zeile beisst, in derselben Liste und
 * nicht im Tagebuch. Ohne diesen Durchgang gingen beide auf dieselbe Etappe und
 * die Kilometer des Tages wären doppelt.
 *
 * Die Liste ist zeitlich sortiert (siehe ersteZuordnung), der Durchgang folgt
 * ihr: von zwei Stücken derselben Fahrt bleibt das frühere stehen, und das
 * spätere ist das, an dem der Einwand steht.
 *
 * @param entscheideNeu Für welche Zeilen der Haken neu gesetzt wird. Beim
 *   ersten Vorschlag für alle; nachdem der Nutzer eine Etappe gewechselt hat,
 *   nur für die eine Zeile - was er selbst angehakt hat, bleibt angehakt, auch
 *   wenn es strittig ist.
 */
function bewerteReihum(
  zeilen: Zuordnung[],
  diary: Record<string, DiaryEntry>,
  entscheideNeu: (z: Zuordnung) => boolean,
): Zuordnung[] {
  const vergeben = new Map<string, TrackPart[]>()

  return zeilen.map((z) => {
    const art = bewerte(z.track, z.stageId, diary, vergeben.get(z.stageId) ?? [])
    const aktiv = entscheideNeu(z) ? art === 'neu' || art === 'ergänzt' : z.aktiv
    if (aktiv && z.stageId) {
      vergeben.set(z.stageId, [...(vergeben.get(z.stageId) ?? []), ...trackParts(z.track)])
    }
    return { ...z, art, aktiv }
  })
}

/**
 * Der Vorschlag, mit dem die Liste aufgeht.
 *
 * Vorgewählt ist nur, was unstrittig ist: ein Stück mit einer Etappe, auf der
 * es entweder noch nichts gibt oder etwas, das sich nicht damit überschneidet.
 * Alles andere steht da, mit Grund, und wartet auf eine Entscheidung. Ein
 * Fehlgriff trifft hier sechzehn Etappen auf einmal - was ungefragt geschieht,
 * muss deshalb harmlos sein.
 *
 * Die Reihenfolge ist die der Uhr und nicht die der Dateien (nachFahrtzeit):
 * wer den Nachmittag vor dem Vormittag wählt, bekommt trotzdem den Tag, wie er
 * gefahren wurde. Stücke ohne Zeitstempel hängen sich hinten an - sie haben
 * keine Stelle in der Zeit, und sie bekommen aus demselben Grund weder ein
 * Datum noch eine Etappe vorgeschlagen.
 *
 * Das Datum eines Stücks ist der Tag seines ersten Zeitstempels in der
 * Ortszeit dieses Geräts - dieselbe Rechnung, mit der die Aufzeichnung einer
 * einzelnen Etappe ihr Datum vorschlägt (isoDate). Die Zeitstempel selbst sind
 * UTC (siehe parseTime), und ein Stück fängt am Morgen an: zwischen Bern und
 * Bukarest liegen zwei Stunden, der Tag ist in beiden Zonen derselbe. Wo das im
 * Grenzfall nicht mehr stimmt - Abfahrt kurz nach Mitternacht -, steht in der
 * Liste eine Auswahl, und die kann der Nutzer ändern.
 *
 * @param tage Die Stücke aller gewählten Dateien, in beliebiger Reihenfolge.
 */
export function ersteZuordnung(
  tage: DiaryTrack[],
  trip: Trip,
  diary: Record<string, DiaryEntry>,
): Zuordnung[] {
  const etappen = stagesByDate(trip, diary)

  const zeilen = nachFahrtzeit(tage).map((track, index): Zuordnung => {
    const datum = track.startedAt !== null ? isoDate(track.startedAt) : ''
    const stageId = (datum ? etappen.get(datum)?.id : '') ?? ''
    return { index, track, datum, stageId, art: 'ohne-etappe', aktiv: false }
  })

  return bewerteReihum(zeilen, diary, () => true)
}

/**
 * Rechnet die Beurteilungen neu, nachdem der Nutzer etwas verstellt hat.
 *
 * Nötig, weil die Zeilen voneinander abhängen: wer ein Stück von einer Etappe
 * nimmt, macht das Stück darunter unstrittig, und wer zwei auf dieselbe legt,
 * macht das zweite strittig. Beides muss dastehen, bevor gedrückt wird, und
 * nicht danach.
 *
 * @param frisch Die Zeile, deren Haken neu entschieden wird - die, deren Etappe
 *   gerade gewechselt wurde. -1 heisst: alle Haken bleiben, wie sie sind.
 */
export function neuBewertet(
  zeilen: Zuordnung[],
  diary: Record<string, DiaryEntry>,
  frisch = -1,
): Zuordnung[] {
  return bewerteReihum(zeilen, diary, (z) => z.index === frisch)
}

/** Was auf einer Etappe geschrieben werden soll. */
export interface Schreibauftrag {
  stageId: string
  track: DiaryTrack
}

/**
 * Macht aus den angehakten Zeilen die Schreibvorgänge.
 *
 * Je Etappe genau einer, auch wenn zwei Stücke auf dieselbe Etappe gehen: der
 * Tagebucheintrag wird als Ganzes geschrieben, ein zweiter Aufruf im selben
 * Durchgang würde den ersten überschreiben - genau der Fehler, um den es hier
 * geht, nur eine Ebene höher.
 *
 * Was auf der Etappe schon liegt, bleibt liegen: die vorhandenen Teile stehen
 * vorne in der Liste, das neue Stück kommt dazu, und zusammengelegt wird nach
 * der Uhr. Angehakt bleibt angehakt - auch eine Zeile mit Konflikt, wenn der
 * Nutzer sie ausdrücklich angehakt hat. Was sie anrichtet, steht in ihrer
 * eigenen Zeile.
 */
export function anwenden(
  zuordnungen: Zuordnung[],
  diary: Record<string, DiaryEntry>,
): Schreibauftrag[] {
  const teile = new Map<string, TrackPart[]>()

  for (const z of zuordnungen) {
    if (!z.aktiv || !z.stageId) continue

    const bisher = teile.get(z.stageId)
    if (bisher) {
      bisher.push(...trackParts(z.track))
      continue
    }

    const vorhanden = diary[z.stageId]?.track
    teile.set(z.stageId, [...(vorhanden ? trackParts(vorhanden) : []), ...trackParts(z.track)])
  }

  const auftraege: Schreibauftrag[] = []
  for (const [stageId, liste] of teile) {
    const track = mergeTrackParts(liste)
    if (track) auftraege.push({ stageId, track })
  }
  return auftraege
}
